export function getValidDateTime(dateValue: string | null | undefined): number | null {
  if (!dateValue) {
    return null;
  }

  const dateTime = new Date(dateValue).getTime();
  return Number.isFinite(dateTime) ? dateTime : null;
}

/** Read part count from list/detail/clone API rows (camelCase or PascalCase). */
export function resolvePartCount(row: unknown): number {
  const r = row as Record<string, unknown> | null | undefined;
  if (!r) {
    return 1;
  }
  const n = Number(
    r['partsCount'] ?? r['PartsCount'] ?? r['partCount'] ?? r['PartCount'] ?? 1
  );
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
}

/** True when multiple parts live under a single RFQ envelope (legacy multipart). */
export function isLegacyMultiPartEnvelope(row: unknown): boolean {
  return resolvePartCount(row) > 1;
}

/**
 * Legacy awarding: multipart RFQs created before `legacyAwardingCutoffDate` keep the old flow.
 * Single-part RFQs always use the new quotes workspace.
 */
export function isLegacyAwardingRfq(
  row: unknown,
  legacyAwardingCutoffDate: string | null | undefined
): boolean {
  if (!isLegacyMultiPartEnvelope(row)) {
    return false;
  }

  const r = row as Record<string, unknown> | null | undefined;
  const createdRaw = r?.['rfqCreatedOn'] ?? r?.['RfqCreatedOn'];
  const rfqCreatedTime = getValidDateTime(typeof createdRaw === 'string' ? createdRaw : undefined);
  const cutoffTime = getValidDateTime(legacyAwardingCutoffDate);
  if (rfqCreatedTime === null || cutoffTime === null) {
    return false;
  }

  return rfqCreatedTime < cutoffTime;
}

/**
 * Whether a cloned legacy multipart RFQ should split on rfq-ai publish.
 * Prefers backend `requiresSplitOnPublish` on the clone response; falls back to source part count.
 */
export function shouldSplitLegacyMultipartOnPublish(
  cloneResult: unknown,
  sourceRow?: unknown
): boolean {
  const r = cloneResult as Record<string, unknown> | null | undefined;
  if (r) {
    const flag = r['requiresSplitOnPublish'] ?? r['RequiresSplitOnPublish'];
    if (flag === true) {
      return true;
    }
  }
  if (sourceRow !== undefined && isLegacyMultiPartEnvelope(sourceRow)) {
    return true;
  }
  return false;
}

/** Query params for navigating to rfq-ai after a successful clone (Yes path). */
export function buildRfqAiCloneQueryParams(
  encryptedRfqId: string,
  cloneResult: unknown,
  sourceRow?: unknown
): { rfqId: string; splitOnPublish?: string } {
  const queryParams: { rfqId: string; splitOnPublish?: string } = {
    rfqId: encryptedRfqId
  };
  if (shouldSplitLegacyMultipartOnPublish(cloneResult, sourceRow)) {
    queryParams.splitOnPublish = '1';
  }
  return queryParams;
}

/** Parse splitOnPublish from router query params. */
export function parseSplitOnPublishQueryParam(value: string | null): boolean {
  if (!value) {
    return false;
  }
  const normalized = value.trim().toLowerCase();
  return normalized === '1' || normalized === 'true';
}
